import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiErrorMessage } from '../api/client';
import * as ordersApi from '../api/ordersApi';
import { CourierOption, Order, RemainingItem, ShipmentResult } from '../types/order';
import { formatINR, shortId } from '../utils/format';
import { Modal, Spinner } from './ui';
import { IconCheck, IconExternal } from './icons';

/**
 * Shiprocket shipment creation flow. When the order has more than one unit still
 * to ship, an item picker appears so the admin can split the order into multiple
 * shipments (each its own AWB). Single-unit orders stay one-click.
 *
 * Phases: loading → select → creating → success → error.
 */
type Phase = 'loading' | 'select' | 'creating' | 'success' | 'error';

const AUTO = 'auto'; // null courier id = auto-assign (best rate)

export default function ShipmentModal({
  open,
  order,
  onClose,
  onShipped,
}: {
  open: boolean;
  order: Order;
  onClose: () => void;
  onShipped: () => void;
}) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [couriers, setCouriers] = useState<CourierOption[]>([]);
  const [selected, setSelected] = useState<string>(AUTO);
  const [error, setError] = useState('');
  const [result, setResult] = useState<ShipmentResult | null>(null);
  // Units chosen per order item (keyed by order_item_id). 0 = exclude.
  const [units, setUnits] = useState<Record<string, number>>({});
  // Whether a create was ATTEMPTED. A failed attempt still leaves a half-created
  // shipment row behind, so the page has to reload to show it — see handleClose.
  const [attempted, setAttempted] = useState(false);

  const addr = order.shipping_address;

  // Items with units still to ship.
  const shippable = useMemo<RemainingItem[]>(
    () => (order.remaining_items ?? []).filter((r) => r.remaining > 0),
    [order.remaining_items]
  );
  // Show the picker only when there's more than one unit to allocate.
  const needsPicker =
    shippable.length > 1 || shippable.some((r) => r.remaining > 1);

  const loadCouriers = useCallback(async () => {
    setPhase('loading');
    setError('');
    try {
      const list = await ordersApi.getCourierRecommendations(order.id);
      setCouriers(list);
      setSelected(AUTO);
      // Default: ship everything remaining.
      setUnits(Object.fromEntries(shippable.map((r) => [r.order_item_id, r.remaining])));
      setPhase('select');
    } catch (err) {
      setError(apiErrorMessage(err));
      setPhase('error');
    }
  }, [order.id, shippable]);

  useEffect(() => {
    if (open) loadCouriers();
  }, [open, loadCouriers]);

  const selectedUnitCount = Object.values(units).reduce((s, n) => s + (n || 0), 0);

  const setItemUnits = (id: string, value: number, max: number) =>
    setUnits((prev) => ({ ...prev, [id]: Math.max(0, Math.min(max, value)) }));

  const handleCreate = async () => {
    setPhase('creating');
    setError('');
    setAttempted(true);
    try {
      const courierId = selected === AUTO ? null : Number(selected);
      // Only pass an item allocation when the picker is in use; otherwise ship all.
      const items = needsPicker
        ? shippable
            .filter((r) => (units[r.order_item_id] ?? 0) > 0)
            .map((r) => ({ order_item_id: r.order_item_id, units: units[r.order_item_id] }))
        : undefined;
      const res = await ordersApi.createShipment(order.id, courierId, items);
      setResult(res);
      setPhase('success');
    } catch (err) {
      setError(apiErrorMessage(err));
      setPhase('error');
    }
  };

  const handleClose = () => {
    // Reload whenever a create was attempted, not only when it succeeded: a failed
    // attempt leaves a half-created shipment the Shipping panel must show — with
    // its Retry / Sync actions — instead of the pre-attempt state.
    if (phase === 'success' || attempted) onShipped();
    setAttempted(false);
    onClose();
  };

  return (
    <Modal open={open} title="Create shipment" onClose={handleClose}>
      {phase === 'loading' && (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <Spinner className="h-6 w-6 text-gold" />
          <p className="text-sm text-sub">Fetching available couriers for this pincode…</p>
        </div>
      )}

      {phase === 'select' && (
        <div className="space-y-5">
          {/* Item picker — only when there's more than one unit to allocate */}
          {needsPicker && (
            <div>
              <label className="label">Items in this shipment</label>
              <div className="space-y-2 rounded-xl border border-edge p-3">
                {shippable.map((r) => {
                  const val = units[r.order_item_id] ?? 0;
                  return (
                    <div key={r.order_item_id} className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{r.product_name}</p>
                        <p className="text-xs text-sub">{r.remaining} of {r.ordered} left to ship</p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          className="btn-ghost !px-2 !py-0.5"
                          onClick={() => setItemUnits(r.order_item_id, val - 1, r.remaining)}
                          disabled={val <= 0}
                        >
                          −
                        </button>
                        <span className="w-6 text-center text-sm font-medium">{val}</span>
                        <button
                          type="button"
                          className="btn-ghost !px-2 !py-0.5"
                          onClick={() => setItemUnits(r.order_item_id, val + 1, r.remaining)}
                          disabled={val >= r.remaining}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="mt-1.5 text-xs text-sub">
                Ship a subset now and the rest later — each shipment gets its own AWB.
              </p>
            </div>
          )}

          <div>
            <label className="label">Courier</label>
            <select className="input" value={selected} onChange={(e) => setSelected(e.target.value)}>
              <option value={AUTO}>⚡ Auto (Best Surface Rate)</option>
              {couriers.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.name} — {formatINR(c.rate)} — {c.estimated_delivery_days} days
                </option>
              ))}
            </select>
            {couriers.length === 0 ? (
              <p className="mt-1.5 text-xs text-warn">
                No surface courier is serviceable for this pincode. Perfume can't ship by air, so
                creating a shipment will fail — check serviceability in Shiprocket.
              </p>
            ) : (
              <p className="mt-1.5 text-xs text-sub">
                Surface couriers only — air freight is restricted for alcohol-based perfume.
              </p>
            )}
          </div>

          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-xl border border-edge bg-edge/20 px-4 py-3 text-sm">
            <dt className="text-sub">Order</dt>
            <dd className="text-right font-medium">{shortId(order.id)}</dd>
            <dt className="text-sub">Customer</dt>
            <dd className="text-right font-medium">{addr?.full_name ?? '—'}</dd>
            <dt className="text-sub">Destination</dt>
            <dd className="text-right font-medium">{addr?.pincode ?? '—'}</dd>
            {needsPicker && (
              <>
                <dt className="text-sub">Units this shipment</dt>
                <dd className="text-right font-medium">{selectedUnitCount}</dd>
              </>
            )}
          </dl>

          <div className="flex justify-end gap-3">
            <button className="btn-outline" onClick={handleClose}>
              Cancel
            </button>
            <button
              className="btn-gold"
              onClick={handleCreate}
              disabled={needsPicker && selectedUnitCount === 0}
            >
              Create Shipment →
            </button>
          </div>
        </div>
      )}

      {phase === 'creating' && (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <Spinner className="h-6 w-6 text-gold" />
          <p className="text-sm text-sub">Creating shipment and generating label…</p>
        </div>
      )}

      {phase === 'success' && result && (
        <div className="space-y-5">
          <div className="flex flex-col items-center gap-3 py-2 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-ok/15 text-ok">
              <IconCheck className="h-6 w-6" />
            </span>
            <p className="text-sm font-medium">Shipment #{result.seq} created</p>
          </div>

          <dl className="space-y-2 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-sub">AWB number</dt>
              <dd className="font-medium">{result.awb_number}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-sub">Courier</dt>
              <dd className="font-medium">{result.courier_name ?? '—'}</dd>
            </div>
          </dl>

          <div className="flex justify-end gap-3">
            {result.label_url && (
              <a className="btn-outline" href={result.label_url} target="_blank" rel="noreferrer">
                Download Label <IconExternal className="h-3.5 w-3.5" />
              </a>
            )}
            <button className="btn-gold" onClick={handleClose}>
              Close
            </button>
          </div>
        </div>
      )}

      {phase === 'error' && (
        <div className="space-y-5">
          <div className="rounded-xl border border-bad/40 bg-bad/10 px-4 py-3 text-sm text-bad">
            {error || 'Something went wrong while creating the shipment.'}
          </div>
          <div className="flex justify-end gap-3">
            <button className="btn-outline" onClick={handleClose}>
              Cancel
            </button>
            <button className="btn-gold" onClick={loadCouriers}>
              Try Again
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
